import { useEffect, useMemo, useState } from 'react';
import { ApiService } from 'services/ApiService';
import type { TattooCategory } from 'models/tattoos/TattooCategory';
import type { TattooStyle } from 'models/tattoos/TattooStyle';

export interface ArtistTattooStylesProps {
  artistId: string;
  isVisitor: boolean;
}

const ArtistTattooStyles: React.FC<ArtistTattooStylesProps> = ({ artistId, isVisitor }) => {
  const [categories, setCategories] = useState<TattooCategory[]>([]);
  const [styles, setStyles] = useState<TattooStyle[]>([]);
  const [assignedStyleIds, setAssignedStyleIds] = useState<Set<number>>(new Set());

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const [isEditing, setIsEditing] = useState(false);
  const [draftStyleIds, setDraftStyleIds] = useState<Set<number>>(new Set());
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | 'all'>('all');

  const styleById = useMemo(() => {
    const map = new Map<number, TattooStyle>();
    styles.forEach((s) => map.set(s.styleID, s));
    return map;
  }, [styles]);

  const categoryById = useMemo(() => {
    const map = new Map<number, TattooCategory>();
    categories.forEach((c) => map.set(c.categoryID, c));
    return map;
  }, [categories]);

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [catsRes, stylesRes, artistRes] = await Promise.all([
        ApiService.getTattooCategories(),
        ApiService.getTattooStyles(),
        ApiService.getArtistTattooStyles(artistId),
      ]);

      if (!catsRes.success || !catsRes.data) throw new Error('Failed to load categories');
      if (!stylesRes.success || !stylesRes.data) throw new Error('Failed to load styles');
      if (!artistRes.success || !artistRes.data) throw new Error("Failed to load artist's styles");

      setCategories(catsRes.data);
      setStyles(stylesRes.data);

      const ids = new Set<number>((artistRes.data || []).map((a) => a.styleID));
      setAssignedStyleIds(ids);
      if (!isEditing) {
        setDraftStyleIds(new Set(ids));
      }
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Failed to load tattoo styles');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!artistId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistId]);

  const assignedGroups = useMemo(() => {
    const groups = new Map<number, { categoryName: string; styles: TattooStyle[] }>();

    Array.from(assignedStyleIds).forEach((styleId) => {
      const s = styleById.get(styleId);
      if (!s) return;

      const categoryId = s.categoryID;
      const categoryName = s.categoryName ?? categoryById.get(categoryId)?.categoryName ?? 'Uncategorized';

      const existing = groups.get(categoryId);
      if (!existing) {
        groups.set(categoryId, { categoryName, styles: [s] });
      } else {
        existing.styles.push(s);
      }
    });

    // Sort groups and styles for stable UI
    return Array.from(groups.entries())
      .sort((a, b) => a[1].categoryName.localeCompare(b[1].categoryName))
      .map(([categoryId, group]) => ({
        categoryId,
        categoryName: group.categoryName,
        styles: group.styles.sort((a, b) => a.styleName.localeCompare(b.styleName)),
      }));
  }, [assignedStyleIds, categoryById, styleById]);

  const availableStyles = useMemo(() => {
    const list = selectedCategoryId === 'all'
      ? styles
      : styles.filter((s) => s.categoryID === selectedCategoryId);

    return list.slice().sort((a, b) => a.styleName.localeCompare(b.styleName));
  }, [selectedCategoryId, styles]);

  const toggleDraftStyle = (styleId: number) => {
    setDraftStyleIds((prev) => {
      const next = new Set(prev);
      if (next.has(styleId)) next.delete(styleId);
      else next.add(styleId);
      return next;
    });
  };

  const handleEdit = () => {
    setDraftStyleIds(new Set(assignedStyleIds));
    setSelectedCategoryId('all');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraftStyleIds(new Set(assignedStyleIds));
    setIsEditing(false);
  };

  const handleSave = async () => {
    const original = assignedStyleIds;
    const draft = draftStyleIds;

    const toAdd = Array.from(draft).filter((id) => !original.has(id));
    const toRemove = Array.from(original).filter((id) => !draft.has(id));

    setIsLoading(true);
    setError('');

    try {
      await Promise.all([
        ...toAdd.map((styleID) => ApiService.addTattooStyleToArtist(artistId, { styleID })),
        ...toRemove.map((styleID) => ApiService.removeArtistTattooStyleByStyleId(artistId, styleID)),
      ]);

      setIsEditing(false);
      await load();
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Failed to save styles');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-700">{error}</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-md rounded-xl space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tattoo Styles</h2>
          <p className="text-sm text-gray-600">Categories and styles for this artist</p>
        </div>

        {!isVisitor && !isEditing && (
          <button
            onClick={handleEdit}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition duration-200 text-sm"
          >
            Edit
          </button>
        )}
      </div>

      {!isEditing && (
        <div className="space-y-4">
          {assignedGroups.length === 0 ? (
            <div className="text-gray-700">No styles listed yet.</div>
          ) : (
            assignedGroups.map((group) => (
              <div key={group.categoryId} className="border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{group.categoryName}</h3>
                <div className="flex flex-wrap gap-2">
                  {group.styles.map((s) => (
                    <span
                      key={s.styleID}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm"
                    >
                      {s.styleName}
                    </span>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {isEditing && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Category</label>
              <select
                value={selectedCategoryId}
                onChange={(e) => {
                  const next = e.target.value;
                  setSelectedCategoryId(next === 'all' ? 'all' : Number(next));
                }}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All</option>
                {categories
                  .slice()
                  .sort((a, b) => a.categoryName.localeCompare(b.categoryName))
                  .map((c) => (
                    <option key={c.categoryID} value={c.categoryID}>
                      {c.categoryName}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleSave}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition duration-200 text-sm"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-100 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-200 transition duration-200 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {availableStyles.map((s) => (
                <label
                  key={s.styleID}
                  className="flex items-start gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={draftStyleIds.has(s.styleID)}
                    onChange={() => toggleDraftStyle(s.styleID)}
                    className="mt-1"
                  />
                  <span>
                    <div className="text-sm font-medium text-gray-900">{s.styleName}</div>
                    {s.description && <div className="text-xs text-gray-600">{s.description}</div>}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistTattooStyles;
